import { useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import IMG from "./push-ex.png";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { AdminSearchListDetailLayout } from "../components/admin-search-list-detail-layout";
import type {
  SearchUsersForCouponQuery,
  SearchUsersForCouponQueryVariables,
  GetCouponsQuery,
  IssueCouponMutation,
  IssueCouponMutationVariables,
} from "../../../__generated__/graphql";

const SEARCH_USERS = gql(`
  query SearchUsersForCoupon($keyword: String) {
    searchUsers(keyword: $keyword) {
      id
      name
      email
      phone
    }
  }
`);

const ISSUE_COUPON = gql(`
  mutation IssueCoupon(
    $userId: Int!
    $couponId: Int!
    $message: String
    $expiredAt: DateTime
  ) {
    issueCoupon(
      userId: $userId
      couponId: $couponId
      message: $message
      expiredAt: $expiredAt
    ) {
      ok
      error
    }
  }
`);

const GET_COUPONS = gql(`
  query GetCoupons {
    getCoupons {
      id
      title
      description
      discountType
      discountValue
      minOrderPrice
      maxDiscountAmount
      startAt
      endAt
      isPublic
    }
  }
`);

type UserRow = NonNullable<
  NonNullable<SearchUsersForCouponQuery["searchUsers"]>[number]
>;

function formatCouponEndAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** datetime-local 값을 ISO로 변환 (로컬 해석). */
function localDateTimeInputToISO(localValue: string): string {
  const d = new Date(localValue);
  return d.toISOString();
}

const CouponIssue = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  /** 비우면 서버에서 쿠폰 템플릿 종료일과 동일하게 저장 */
  const [customExpiredAtLocal, setCustomExpiredAtLocal] = useState("");
  const [pushMessage, setPushMessage] = useState("");

  const [searchUsers, { data: userData, loading: userLoading }] = useLazyQuery<
    SearchUsersForCouponQuery,
    SearchUsersForCouponQueryVariables
  >(SEARCH_USERS, { fetchPolicy: "network-only" });

  const { data: couponData, loading: couponLoading } =
    useQuery<GetCouponsQuery>(GET_COUPONS);

  const [issueCoupon, { loading: issuing }] = useMutation<
    IssueCouponMutation,
    IssueCouponMutationVariables
  >(ISSUE_COUPON, {
    onCompleted: (res) => {
      if (res.issueCoupon.ok) {
        notify("쿠폰이 발행되었습니다.");
        setSelectedUser(null);
        setSelectedCouponId(null);
        setCustomExpiredAtLocal("");
        setPushMessage("");
      } else {
        notifyError(res.issueCoupon.error ?? "쿠폰 발행에 실패했습니다.");
      }
    },
    onError: () => {
      notifyError("쿠폰 발행 중 오류가 발생했습니다.");
    },
  });

  const handleSearch = () => {
    const term = searchKeyword.trim();
    if (!term) {
      notifyError("검색어를 입력해주세요.");
      return;
    }
    searchUsers({ variables: { keyword: term } });
  };

  const handleIssueCoupon = () => {
    if (!selectedUser || !selectedCouponId) {
      notifyError("유저와 쿠폰을 선택해주세요.");
      return;
    }
    const selected = coupons.find((c) => c.id === selectedCouponId);
    if (customExpiredAtLocal.trim() && selected) {
      const chosen = new Date(localDateTimeInputToISO(customExpiredAtLocal));
      const end = new Date(selected.endAt);
      if (chosen.getTime() > end.getTime()) {
        notifyError("만료일은 쿠폰 종료일을 넘을 수 없습니다.");
        return;
      }
    }
    issueCoupon({
      variables: {
        userId: selectedUser.id,
        couponId: selectedCouponId,
        message: pushMessage || undefined,
        expiredAt: customExpiredAtLocal.trim()
          ? localDateTimeInputToISO(customExpiredAtLocal)
          : undefined,
      },
    });
  };

  const users = (userData?.searchUsers ?? []).filter(
    (u): u is UserRow => u != null
  );
  const coupons = couponData?.getCoupons ?? [];
  const selectedCoupon = selectedCouponId
    ? coupons.find((c) => c.id === selectedCouponId)
    : undefined;

  if (couponLoading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }

  const listContent =
    userLoading && users.length === 0 ? (
      <div className="flex justify-center py-8">
        <LoadingSpinnerInline />
      </div>
    ) : users.length === 0 ? (
      <div className="p-6 text-center text-gray-500 text-sm">
        검색어를 입력하고 검색 버튼을 누르세요.
      </div>
    ) : (
      <ul className="divide-y divide-gray-200">
        {users.map((user) => {
          const isSelected = selectedUser?.id === user.id;
          return (
            <li
              key={user.id}
              onClick={() => setSelectedUser(user)}
              className={clsx(
                "px-4 py-3 cursor-pointer transition-colors",
                isSelected
                  ? "bg-blue-50 border-l-4 border-l-blue-600"
                  : "hover:bg-gray-50"
              )}
            >
              <div className="font-medium text-gray-900">
                {user.name ?? "이름 없음"} (ID: {user.id})
              </div>
              <div className="text-xs text-gray-500 truncate">
                {user.email}
                {user.phone ? ` · ${user.phone}` : ""}
              </div>
            </li>
          );
        })}
      </ul>
    );

  const rightPanelContent = selectedUser ? (
    <div className="space-y-4">
      <div className="text-sm text-gray-700">
        <span className="font-medium">선택 유저:</span>{" "}
        {selectedUser.name ?? "이름 없음"} ({selectedUser.email ?? "—"})
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          쿠폰 선택
        </label>
        <select
          value={selectedCouponId ?? ""}
          onChange={(e) => {
            setCustomExpiredAtLocal("");
            setSelectedCouponId(
              e.target.value ? Number(e.target.value) : null
            );
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">쿠폰 선택</option>
          {coupons.map((coupon) => (
            <option key={coupon.id} value={coupon.id}>
              {coupon.title} - {coupon.description}
            </option>
          ))}
        </select>
        {selectedCoupon ? (
          <p className="mt-1 text-xs text-gray-500">
            템플릿 종료(사용 만료 상한):{" "}
            <span className="font-medium text-gray-700">
              {formatCouponEndAt(selectedCoupon.endAt)}
            </span>
          </p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          이번 발급 만료일 (선택)
        </label>
        <input
          type="datetime-local"
          value={customExpiredAtLocal}
          onChange={(e) => setCustomExpiredAtLocal(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          비우면 위 템플릿 종료일과 동일하게 저장됩니다. 채우면 그 시각까지
          유효하며, 쿠폰 종료일을 넘길 수 없습니다.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          푸시 메시지 (선택)
        </label>
        <textarea
          placeholder="죄송한 마음에 쿠폰을 드립니다 🥲"
          value={pushMessage}
          onChange={(e) => setPushMessage(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:ring-blue-500 focus:border-blue-500"
          rows={4}
        />
      </div>

      <img
        src={IMG}
        alt="푸시 예시"
        className="w-full rounded-lg border border-gray-200"
      />
      <p className="text-xs text-gray-500">
        * &apos;쿠폰이 발급되었습니다.&apos;는 고정, 본문만 수정 가능
      </p>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => setSelectedUser(null)}
          className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200 text-sm"
        >
          선택 해제
        </button>
        <button
          type="button"
          onClick={handleIssueCoupon}
          disabled={!selectedCouponId || issuing}
          className="flex-1 px-4 py-2 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          {issuing ? (
            <>
              <LoadingSpinnerInline />
              발행 중...
            </>
          ) : (
            "쿠폰 발행"
          )}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <AdminSearchListDetailLayout
      title="쿠폰 발급"
      description="유저를 검색·선택하고 쿠폰을 선택한 뒤 발행하세요."
      leftRatio={7}
      rightRatio={3}
      searchPlaceholder="이름, 이메일, 번호로 검색"
      searchValue={searchKeyword}
      onSearchChange={setSearchKeyword}
      onSearch={handleSearch}
      searchLoading={userLoading}
      step1Title="1. 유저 검색"
      step2Title="2. 검색 결과에서 선택"
      step3Title="3. 쿠폰 발행"
      listContent={listContent}
      emptyListText="검색어를 입력하고 검색 버튼을 누르세요."
      rightPanelContent={rightPanelContent}
      rightPanelEmptyText="유저를 선택하세요."
    />
  );
};

export default CouponIssue;
